import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import { Box } from '@mui/system';
import { Button } from '@openx/components-core/lib/Button/Button.tsx';
import { ELEVATION } from '@openx/components-core/lib/theme/elevation.ts';
import type { CumulativeBundleSegment, CumulativeDomainSegment } from '@openx/types/gqlTypes.ts';
import { usePermissionContext } from '@openx/utils/permission/context/PermissionContext.tsx';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDealPrompt } from '../../hooks/useDealPrompt.ts';
import { useDealPromptCreateDeal } from '../../hooks/useDealPromptCreateDeal.ts';
import { getDealPromptAudienceDealTargetAccount } from '../../utils/getDealPromptAudienceDealTargetAccount.ts';
import {
  type AudienceSegmentSetWithSegments,
  isAudienceSegmentSetWithSegmentsTile,
  isCumulativeDealPromptTileItem,
  toAudienceSegmentSet,
} from '../../utils/getTilesData.ts';
import { hasNonSellableAudienceSegmentsFromMultipleAccounts } from '../../utils/hasNonSellableAudienceSegmentsFromMultipleAccounts.ts';
import { isExplicitNonUsRegion } from '../../utils/isExplicitNonUsRegion.ts';
import { mapMetricsToTileStats } from '../../utils/mapMetricsToTileStats.ts';
import { createNormalizedDefinitionFromSegmentSetAndSelection } from '../../utils/normalizedDefinitionFromAudienceItems.ts';
import { dealPromptContextTypes } from '../../utils/types.ts';
import { DealPromptAudienceCreateDealDialog } from '../DealPromptAudienceCreateDealDialog.tsx';
import { DealPromptAudienceTileStats } from './DealPromptAudienceTileStats.tsx';
import { DealPromptTileHeader, type DealPromptTileHeaderProps } from './DealPromptTileHeader.tsx';
import { DealPromptTileSkeleton } from './DealPromptTileSkeleton.tsx';
import { DealPromptTileStats } from './DealPromptTileStats.tsx';

export type DealPromptTileDetailsPayload = {
  title: string;
  item: CumulativeDomainSegment | CumulativeBundleSegment | AudienceSegmentSetWithSegments | null;
};

type DealPromptTileProps = {
  item: CumulativeDomainSegment | CumulativeBundleSegment | AudienceSegmentSetWithSegments | null;
} & DealPromptTileHeaderProps;

export const DealPromptTile = ({ item, ...headerProps }: DealPromptTileProps) => {
  const { t } = useTranslation();
  const {
    state: { loading: isLoading, contextType },
    openDetails: onOpenDetails,
    openDetailsForAudience: onOpenDetailsForAudience,
  } = useDealPrompt();

  const { session } = usePermissionContext();
  const currentUserAccountId = session?.account?.external_id;

  const { isAudienceCreateDealOpen, handleCreateDeal, closeAudienceCreateDealDialog, submitAudienceCreateDeal } =
    useDealPromptCreateDeal();

  const stats = useMemo(() => mapMetricsToTileStats(item?.metrics), [item?.metrics]);

  const onOpenDetailsHandler = useCallback(() => {
    if (!item) return;
    if (contextType === dealPromptContextTypes.AUDIENCE && isAudienceSegmentSetWithSegmentsTile(item)) {
      onOpenDetailsForAudience({ title: headerProps.title, item });
    } else if (isCumulativeDealPromptTileItem(item)) {
      onOpenDetails({ title: headerProps.title, item });
    }
  }, [contextType, headerProps.title, item, onOpenDetails, onOpenDetailsForAudience]);

  const renderStats = useCallback(() => {
    if (contextType === dealPromptContextTypes.AUDIENCE) {
      const numberOfSegments =
        item && isAudienceSegmentSetWithSegmentsTile(item) ? (item.matchedSegments?.length ?? 0) : undefined;

      return <DealPromptAudienceTileStats {...stats} numberOfSegments={numberOfSegments} />;
    }

    return <DealPromptTileStats {...stats} />;
  }, [contextType, stats, item]);

  const hideCreateDealButton = useMemo(() => {
    if (contextType !== dealPromptContextTypes.AUDIENCE || !item || !isAudienceSegmentSetWithSegmentsTile(item)) {
      return false;
    }
    return hasNonSellableAudienceSegmentsFromMultipleAccounts(item.segments);
  }, [contextType, item]);

  const audienceDealTarget = useMemo(() => {
    if (!item || !isAudienceSegmentSetWithSegmentsTile(item)) {
      return undefined;
    }
    return getDealPromptAudienceDealTargetAccount(item.segments, currentUserAccountId ?? undefined);
  }, [item, currentUserAccountId]);

  const isAudienceSelectionExplicitNonUsRegion = useMemo(() => {
    if (!item || !isAudienceSegmentSetWithSegmentsTile(item)) {
      return false;
    }

    return isExplicitNonUsRegion(createNormalizedDefinitionFromSegmentSetAndSelection(item, item.segments));
  }, [item]);

  const renderCreateDealDialog = useCallback(() => {
    if (contextType === dealPromptContextTypes.AUDIENCE && isAudienceCreateDealOpen) {
      return (
        <DealPromptAudienceCreateDealDialog
          open={isAudienceCreateDealOpen}
          onClose={closeAudienceCreateDealDialog}
          isExplicitNonUsRegion={isAudienceSelectionExplicitNonUsRegion}
          dataPartnerAccountName={audienceDealTarget ? audienceDealTarget.accountName : undefined}
          onSubmit={async form => {
            if (item && 'segments' in item) {
              await submitAudienceCreateDeal({
                form,
                segmentSet: toAudienceSegmentSet(item),
                selectedSegments: item.segments,
                ...(audienceDealTarget
                  ? {
                      audienceAccountIdOverride: audienceDealTarget.audienceAccountId,
                      dealFormAccountUidOverride: audienceDealTarget.dealFormAccountUid,
                    }
                  : {}),
              });
            }
          }}
        />
      );
    }
    return null;
  }, [
    contextType,
    isAudienceCreateDealOpen,
    closeAudienceCreateDealDialog,
    submitAudienceCreateDeal,
    item,
    audienceDealTarget,
    isAudienceSelectionExplicitNonUsRegion,
  ]);

  if (isLoading) return <DealPromptTileSkeleton />;

  if (!item) return null;

  const titleSlug = headerProps.title.toLowerCase().replace(/\s+/g, '-');

  return (
    <Grid sx={{ display: 'flex', maxWidth: '360px', width: '100%', position: 'relative' }}>
      {renderCreateDealDialog()}
      <Card
        role="region"
        sx={{ boxShadow: ELEVATION.elevation0, borderRadius: '4px', width: '100%' }}
        data-test="deal-prompt-tile"
      >
        <CardContent
          data-test={`deal-prompt-${headerProps.title}-tile`}
          sx={{ width: '100%', p: 0, paddingBottom: '16px !important', display: 'flex', flexDirection: 'column' }}
        >
          <DealPromptTileHeader {...headerProps} />

          <Divider />

          {renderStats()}

          <Divider sx={{ width: '92%', alignSelf: 'center' }} />

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              marginTop: '16px',
              width: '92%',
              alignSelf: 'center',
              gap: 1,
            }}
          >
            {!hideCreateDealButton && (
              <Button
                data-test="create-deal-button"
                data-pendo-feature={`deal-prompt-results-${contextType}-create-deal-${titleSlug}`}
                color="primary"
                variant="contained"
                sx={{ alignSelf: 'flex-start' }}
                onClick={() => {
                  handleCreateDeal(item);
                }}
              >
                {t('Create deal')}
              </Button>
            )}

            <Button
              data-test="see-details-button"
              data-pendo-feature={`deal-prompt-results-${contextType}-customize-set-${titleSlug}`}
              variant="text"
              sx={{ color: 'primary.main', alignSelf: 'flex-end' }}
              onClick={onOpenDetailsHandler}
            >
              {t('Customize set')}
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Grid>
  );
};
